import { Alert, CircularProgress } from '@mui/material';
import React, { useCallback, useRef, useState, forwardRef, useEffect } from 'react';
import { useDataStore } from 'app/store/dataStore';
import { IImages, IMessage } from 'src/app/types';
import { useTranslation } from 'react-i18next';
import { BSON as Oid } from "realm-web"
import Compressor from 'compressorjs';
import { beforeUploading } from 'app/store/dataUtils';
interface ChatUploadProps {
  togglePreview: React.Dispatch<React.SetStateAction<boolean>>
}

const ErrorAlert = ({ message }: { message?: string }) => {
  return (
    <Alert icon={<></>} severity='error' variant='outlined' className='!outline-none !border-[color:var(--rc-accent,#2266e3b9)] text-[#11100f]'>{message}</Alert>
  );
};



const ChatImageUpload = forwardRef<HTMLDivElement, ChatUploadProps>(({ togglePreview }, ref) => {
  const { chatMessageDoc, popSelectedImage, selectedImages, setImageDoc } = useDataStore();
  const { t: trl } = useTranslation();
  const fileInputRef = useRef(null);

  const handleCancel = () => {
    togglePreview(false);
    fileInputRef.current && (fileInputRef.current.value = '');
  };

  const handleImageUpload = async () => {
    try {
      const docs = selectedImages.map((image) => ({
        _id: new Oid.ObjectId(),
        image: image,
        message: chatMessageDoc._id
      }));
      setImageDoc(docs);
      handleCancel()
    } catch (error) {
      handleCancel();
    }
  }

  return (
    <div className='w-[80%]  lg:w-[674px] h-[330px] bg-white rounded-[8px] flex flex-col gap-10 p-[16px]'>
      <section id='upload_title' className='flex flex-row justify-between items-center'>
        <h2 className='text-[14px] font-600'> {trl('comms_imageModal_title')} </h2>

        <svg role='button' onClick={handleCancel} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-[20px] h-[20px]">
          <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </section>

      <section
        id="preview_main"
        className="flex flex-row gap-8 items-center min-h-[75%] max-h-[75%]"
      >
        <section
          id="preview_images_zone"
          className="w-full h-full max-h-full flex flex-row gap-6 items-center overflow-x-auto"
        >
          {selectedImages.map((image, index) => (
            <div
              key={index}
              className="flex-none w-1/3 h-full flex flex-col items-center relative"
            >
              <img
                onClick={() => popSelectedImage(index)}
                src={image}
                title="Click to remove"
                className="w-full h-full object-contain rounded-[8px] bg-gray-50"
              />
            </div>
          ))}

          {/* Include the upload zone only if less than 3 images are selected */}
          {selectedImages.length < 3 && (
            <section
              id="upload_drag_zone"
              className={`flex-none h-full ${selectedImages.length === 0 ? 'w-full' : 'w-1/3'
                }`}
            >
              <div
                className="upload-container flex flex-col items-center justify-center h-full"
                id="uploadContainer"
              >
                <svg
                  id="icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-[20px] h-[20px]"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                    clipRule="evenodd"
                  />
                </svg>
                <input
                  type="file"
                  id="fileInput"
                  accept="image/*"
                  multiple={true}
                  max={3}
                  onChange={async (e) => beforeUploading(e.target.files)}
                  ref={fileInputRef}
                />
                <p className="text-[14px] text-gray-400">
                  {trl('comms_imageModal_placeholder')}
                </p>
              </div>
            </section>
          )}
        </section>
      </section>

      <section id='preview_submit_button' className="flex flex-row justify-between items-center bg-white" >
        <button className='flex flex-row items-center justify-center w-[100px] h-[38px] text-[14px] font-600 rounded-[12px] bg-[color:var(--rc-chrome,#010644)] hover:bg-[color:var(--rc-chrome-dark,#010544ce)] disabled:bg-[color:var(--rc-chrome,#010644)] disabled:opacity-40 text-gray-50 disabled:cursor-not-allowed' disabled={selectedImages.length == 0 ? true : false} onClick={() => handleImageUpload()}>
          {trl('comms_imageModal_doneButton')}
        </button>
      </section>
    </div >
  )
})

export default ChatImageUpload;